import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { FunctionsHttpError } from '@supabase/supabase-js';
import { supabase } from '@/lib/supabase';
import { useAuth } from '@/app/AuthProvider';
import { canRead } from '@/lib/permissions';
import type { ExpenseStatus, Json, QbPostStatus, Tables } from '@/types/database';

/**
 * SECURITY: every hook in this file targets a financial table or a Mercury /
 * QuickBooks-derived view. Access is restricted to `super_admin` in the DB
 * (see supabase/migrations/15_financials_super_admin_only.sql). The
 * `enabled` gate here is a UX layer — it stops the browser from firing a
 * request that RLS would reject anyway, which keeps the network tab and
 * Supabase logs clean for non-super users.
 */
function useFinancialsEnabled(): boolean {
  const { role, loading } = useAuth();
  return !loading && canRead(role, 'financials');
}

export function useBankAccounts() {
  const enabled = useFinancialsEnabled();
  return useQuery({
    queryKey: ['bank-accounts'],
    enabled,
    queryFn: async () => {
      const { data, error } = await supabase
        .from('bank_accounts')
        .select('*')
        .order('name');
      if (error) throw error;
      return data as Tables<'bank_accounts'>[];
    },
  });
}

/**
 * Mercury credit/debit cards. Each card belongs to a bank_accounts row;
 * for credit accounts that row holds the credit-line balance, for deposit
 * accounts (rare) it's a debit card on the checking account. We pull the
 * parent account name in via the join so the UI can show "IO Card on
 * Operating" without a second roundtrip.
 */
export function useCards() {
  const enabled = useFinancialsEnabled();
  return useQuery({
    queryKey: ['cards'],
    enabled,
    queryFn: async () => {
      const { data, error } = await supabase
        .from('cards')
        .select('*, bank_accounts(id, name, account_type)')
        .order('issued_at', { ascending: false });
      if (error) throw error;
      return data as Array<
        Tables<'cards'> & {
          bank_accounts: Pick<Tables<'bank_accounts'>, 'id' | 'name' | 'account_type'> | null;
        }
      >;
    },
  });
}

/**
 * The shape returned for each transaction row by `useTransactions`. Exported
 * so call sites (LiveTransactionsTab, Dashboard, etc.) can describe
 * `Array<TransactionListRow>` without re-spelling the join shape.
 */
export type TransactionListRow = Tables<'transactions'> & {
  bank_accounts: Pick<Tables<'bank_accounts'>, 'name' | 'account_type'> | null;
  cards: Pick<Tables<'cards'>, 'id' | 'last_four' | 'name_on_card' | 'network'> | null;
  chart_of_accounts: Pick<
    Tables<'chart_of_accounts'>,
    'id' | 'name' | 'account_type' | 'subtype' | 'code'
  > | null;
  qb_vendor: Pick<Tables<'vendors'>, 'id' | 'name' | 'qb_external_id'> | null;
  qb_customer: Pick<Tables<'customers'>, 'id' | 'name' | 'qb_external_id'> | null;
  /**
   * For rows that are part of a paired internal transfer, this is the OTHER
   * side's row — populated client-side by `useTransactions` so the UI can
   * render "Mercury Checking → Mercury Credit" without a second roundtrip.
   * Null for unpaired rows (the common case) and for the lonely half of a
   * pair where the partner happens to have fallen outside the limit window.
   */
  transfer_partner: {
    id: string;
    bank_account_id: string;
    direction: 'debit' | 'credit';
    posted_at: string;
    bank_accounts: Pick<Tables<'bank_accounts'>, 'name' | 'account_type'> | null;
  } | null;
};

export function useTransactions(params?: {
  limit?: number;
  bankAccountId?: string | null;
  cardId?: string | null;
  category?: string | null;
  /**
   * When true (default), drop the follower side of every paired internal
   * transfer so the list shows one logical entry per pair. The lead
   * (direction='debit') stays; the follower (direction='credit') is
   * filtered out client-side. Set to false to inspect both halves — used
   * by the Live Transactions "Show both sides" toggle and by callers that
   * care about per-account ledgers (e.g. reconciliation).
   *
   * Either way, both rows are present in the database — collapse only
   * affects the read path. The Dashboard cash-flow metrics use a separate
   * v_daily_cash_flow view that filters paired rows in SQL (see
   * migration 72), so this toggle is a pure UI affordance.
   */
  collapseTransfers?: boolean;
}) {
  const enabled = useFinancialsEnabled();
  const collapseTransfers = params?.collapseTransfers ?? true;
  // The query key intentionally does NOT include `collapseTransfers` —
  // collapse is a pure post-process on the same dataset, so flipping the
  // toggle should hit the same cache rather than refetch.
  const fetchKey = {
    limit: params?.limit,
    bankAccountId: params?.bankAccountId ?? null,
    cardId: params?.cardId ?? null,
    category: params?.category ?? null,
  };
  return useQuery({
    queryKey: ['transactions', fetchKey],
    enabled,
    queryFn: async () => {
      let q = supabase
        .from('transactions')
        .select(
          '*, bank_accounts(name, account_type), cards(id, last_four, name_on_card, network), ' +
            'chart_of_accounts(id, name, account_type, subtype, code), ' +
            // qb_vendor / qb_customer carry the picked QBO payee (the "Name"
            // on the Purchase). One side is always null thanks to the
            // transactions_one_qb_payee check; we embed both and let the
            // caller pick whichever is set.
            'qb_vendor:qb_vendor_id(id, name, qb_external_id), ' +
            'qb_customer:qb_customer_id(id, name, qb_external_id)',
        )
        .order('posted_at', { ascending: false })
        .limit(params?.limit ?? 100);
      if (params?.bankAccountId) q = q.eq('bank_account_id', params.bankAccountId);
      if (params?.cardId) q = q.eq('card_id', params.cardId);
      if (params?.category) q = q.eq('category', params.category);
      const { data, error } = await q;
      if (error) throw error;
      return data as unknown as Array<
        Tables<'transactions'> & {
          bank_accounts: Pick<Tables<'bank_accounts'>, 'name' | 'account_type'> | null;
          cards: Pick<Tables<'cards'>, 'id' | 'last_four' | 'name_on_card' | 'network'> | null;
          chart_of_accounts: Pick<
            Tables<'chart_of_accounts'>,
            'id' | 'name' | 'account_type' | 'subtype' | 'code'
          > | null;
          qb_vendor: Pick<Tables<'vendors'>, 'id' | 'name' | 'qb_external_id'> | null;
          qb_customer: Pick<Tables<'customers'>, 'id' | 'name' | 'qb_external_id'> | null;
        }
      >;
    },
    select: (rows): TransactionListRow[] => {
      // Build the partner lookup off the full result so each row knows
      // about the other half even after we drop it from the visible set.
      const byGroup = new Map<string, typeof rows>();
      for (const r of rows) {
        if (!r.transfer_group_id) continue;
        const list = byGroup.get(r.transfer_group_id) ?? [];
        list.push(r);
        byGroup.set(r.transfer_group_id, list);
      }

      const enriched: TransactionListRow[] = rows.map((r) => {
        let partner: TransactionListRow['transfer_partner'] = null;
        if (r.transfer_group_id) {
          const siblings = byGroup.get(r.transfer_group_id) ?? [];
          const other = siblings.find((s) => s.id !== r.id);
          if (other) {
            partner = {
              id: other.id,
              bank_account_id: other.bank_account_id,
              direction: other.direction,
              posted_at: other.posted_at,
              bank_accounts: other.bank_accounts,
            };
          }
        }
        return { ...r, transfer_partner: partner } as TransactionListRow;
      });

      if (!collapseTransfers) return enriched;

      // Collapse: keep one row per transfer_group_id. We pick the lead
      // (direction='debit') as canonical because that's the side
      // qbo-post-transaction posts, and that's the side the auto-matcher
      // pre-categorised — the operator's edits in the grid line up with
      // the row that actually goes to QBO. Unpaired rows always pass
      // through. If the partner happens to be outside the fetch window
      // (rare — the pairs are stamped within ±2 days), the lonely half
      // shows up too rather than being silently hidden.
      const seen = new Set<string>();
      return enriched.filter((r) => {
        if (!r.transfer_group_id) return true;
        if (seen.has(r.transfer_group_id)) return false;
        if (r.direction === 'debit') {
          seen.add(r.transfer_group_id);
          return true;
        }
        // Credit-side: only keep it if there's no debit-side sibling in
        // the visible set (i.e. the partner fell outside `limit`). We
        // detect "no debit sibling" by checking whether any other row
        // with the same group_id has direction='debit'.
        const siblings = byGroup.get(r.transfer_group_id) ?? [];
        const hasDebit = siblings.some((s) => s.direction === 'debit');
        if (hasDebit) return false;
        seen.add(r.transfer_group_id);
        return true;
      });
    },
  });
}

/**
 * Single-transaction detail for the super_admin Transactions detail page.
 * Mirrors the joins of `useTransactions` so the detail view can render
 * card / bank / QB-category context without a second roundtrip. Gated
 * behind the financials module (super_admin only).
 *
 * `transfer_partner` is the other half of a paired internal transfer (see
 * migration 72) so the page can render a "Paired with → Mercury Credit"
 * link without a second roundtrip. Null for unpaired rows; null too in
 * the rare case where the partner row was deleted out from under us.
 */
export type TransferPartner = {
  id: string;
  organization_id: string;
  bank_account_id: string;
  direction: 'debit' | 'credit';
  posted_at: string;
  amount_cents: number;
  description: string;
  description_override: string | null;
  expense_status: ExpenseStatus;
  qb_post_status: QbPostStatus;
  bank_accounts: Pick<Tables<'bank_accounts'>, 'id' | 'name' | 'account_type'> | null;
};

export type TransactionDetail = Tables<'transactions'> & {
  bank_accounts: Pick<Tables<'bank_accounts'>, 'id' | 'name' | 'account_type'> | null;
  cards: Pick<Tables<'cards'>, 'id' | 'last_four' | 'name_on_card' | 'network'> | null;
  chart_of_accounts: Pick<
    Tables<'chart_of_accounts'>,
    'id' | 'name' | 'account_type' | 'subtype' | 'code'
  > | null;
  qb_vendor: Pick<Tables<'vendors'>, 'id' | 'name' | 'qb_external_id'> | null;
  qb_customer: Pick<Tables<'customers'>, 'id' | 'name' | 'qb_external_id'> | null;
  transfer_partner: TransferPartner | null;
};

export function useTransaction(id: string | undefined) {
  const enabled = useFinancialsEnabled() && !!id;
  return useQuery({
    queryKey: ['transaction', id],
    enabled,
    queryFn: async () => {
      const { data, error } = await supabase
        .from('transactions')
        .select(
          '*, bank_accounts(id, name, account_type), ' +
            'cards(id, last_four, name_on_card, network), ' +
            'chart_of_accounts(id, name, account_type, subtype, code), ' +
            'qb_vendor:qb_vendor_id(id, name, qb_external_id), ' +
            'qb_customer:qb_customer_id(id, name, qb_external_id)',
        )
        .eq('id', id!)
        .single();
      if (error) throw error;
      // Look up the partner side if this row is part of a transfer pair.
      // We do this as a follow-up query rather than via a self-join so the
      // primary row's joins stay simple (PostgREST self-joins work but
      // make the .select() string substantially harder to read).
      let partner: TransferPartner | null = null;
      const groupId = (data as unknown as Tables<'transactions'>).transfer_group_id;
      if (groupId) {
        const { data: others, error: pErr } = await supabase
          .from('transactions')
          .select(
            'id, organization_id, bank_account_id, direction, posted_at, ' +
              'amount_cents, description, description_override, expense_status, ' +
              'qb_post_status, bank_accounts(id, name, account_type)',
          )
          .eq('transfer_group_id', groupId)
          .neq('id', id!);
        if (pErr) throw pErr;
        if (others && others.length > 0) {
          partner = others[0] as unknown as TransferPartner;
        }
      }
      return {
        ...(data as unknown as TransactionDetail),
        transfer_partner: partner,
      };
    },
  });
}

/**
 * Manually link two unrelated transactions as a transfer pair. The order
 * of the two ids doesn't matter — `link_transactions_as_transfer` (see
 * migration 72) pins the debit as the lead internally so the
 * auto-categorisation defaults match what the auto-matcher would have
 * done. Used by the "Link to another transaction" affordance on
 * TransactionDetailPage for cases the heuristic missed.
 */
export function useLinkTransactionsAsTransfer() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: { leadId: string; followerId: string }) => {
      const { data, error } = await supabase.rpc('link_transactions_as_transfer', {
        p_lead_id: input.leadId,
        p_follower_id: input.followerId,
      });
      if (error) throw error;
      return data as string;
    },
    onSuccess: (_data, vars) => {
      qc.invalidateQueries({ queryKey: ['transactions'] });
      qc.invalidateQueries({ queryKey: ['transaction', vars.leadId] });
      qc.invalidateQueries({ queryKey: ['transaction', vars.followerId] });
      qc.invalidateQueries({ queryKey: ['expense-transactions'] });
      qc.invalidateQueries({ queryKey: ['daily-cash-flow'] });
    },
  });
}

/**
 * Search candidate transactions to manually pair with `txId` as a transfer.
 * Returns unpaired rows on a different bank account in the same org with
 * the opposite direction, ordered by closeness to the source row's
 * posted_at, then amount delta. The "Link to another transaction" picker
 * on TransactionDetailPage uses this to surface the most-likely candidates
 * without making the operator guess at the date / amount.
 */
export interface TransferLinkCandidate {
  id: string;
  posted_at: string;
  amount_cents: number;
  direction: 'debit' | 'credit';
  description: string;
  description_override: string | null;
  bank_accounts: Pick<Tables<'bank_accounts'>, 'id' | 'name' | 'account_type'> | null;
  // Days between the source row's posted_at and this candidate's. Negative
  // when this candidate is earlier than the source — the picker uses the
  // signed value to render "+1d / -3d" deltas.
  posted_delta_days: number;
  // Cents difference between the source row's amount and this candidate's
  // (always >= 0; the picker renders "$0.05 fee" for non-zero values).
  amount_delta_cents: number;
}

export function useTransferLinkCandidates(
  txId: string | undefined,
  windowDays = 7,
) {
  const enabled = useFinancialsEnabled() && !!txId;
  return useQuery({
    queryKey: ['transfer-link-candidates', txId, windowDays],
    enabled,
    queryFn: async (): Promise<TransferLinkCandidate[]> => {
      // Fetch the source row once so we can do the date-window math
      // client-side (avoids needing to thread it through a dedicated SQL
      // function — the picker is a low-traffic affordance).
      const { data: src, error: srcErr } = await supabase
        .from('transactions')
        .select('id, organization_id, bank_account_id, posted_at, amount_cents, direction')
        .eq('id', txId!)
        .single();
      if (srcErr) throw srcErr;

      const srcPosted = new Date(src.posted_at).getTime();
      const since = new Date(srcPosted - windowDays * 24 * 60 * 60 * 1000).toISOString();
      const until = new Date(srcPosted + windowDays * 24 * 60 * 60 * 1000).toISOString();
      const oppositeDir: 'debit' | 'credit' =
        src.direction === 'debit' ? 'credit' : 'debit';

      const { data, error } = await supabase
        .from('transactions')
        .select(
          'id, posted_at, amount_cents, direction, description, description_override, ' +
            'bank_accounts(id, name, account_type)',
        )
        .eq('organization_id', src.organization_id)
        .neq('bank_account_id', src.bank_account_id)
        .is('transfer_group_id', null)
        .eq('direction', oppositeDir)
        .gte('posted_at', since)
        .lte('posted_at', until)
        .order('posted_at', { ascending: false })
        .limit(50);
      if (error) throw error;

      // Supabase's joined-row return type unions the row shape with its
      // error variant when there's a relationship in the select string
      // — cast to a known shape so the .map() callback doesn't have to
      // narrow per-property.
      type CandidateRow = {
        id: string;
        posted_at: string;
        amount_cents: number;
        direction: 'debit' | 'credit';
        description: string;
        description_override: string | null;
        bank_accounts: TransferLinkCandidate['bank_accounts'];
      };
      const rows = (data ?? []) as unknown as CandidateRow[];
      const candidates: TransferLinkCandidate[] = rows.map((r) => {
        const candidatePosted = new Date(r.posted_at).getTime();
        const deltaMs = candidatePosted - srcPosted;
        const deltaDays = Math.round(deltaMs / (24 * 60 * 60 * 1000));
        return {
          id: r.id,
          posted_at: r.posted_at,
          amount_cents: r.amount_cents,
          direction: r.direction,
          description: r.description,
          description_override: r.description_override,
          bank_accounts: r.bank_accounts,
          posted_delta_days: deltaDays,
          amount_delta_cents: Math.abs(r.amount_cents - src.amount_cents),
        };
      });

      // Rank: exact-amount matches with the smallest |delta_days| first,
      // then non-exact-amount matches by amount delta. The matcher SQL
      // doesn't surface ambiguous-by-amount candidates, but a manual
      // search should — the operator may want to pair $1000.00 with
      // $999.95 + $0.05 fee.
      candidates.sort((a, b) => {
        const aExact = a.amount_delta_cents === 0 ? 0 : 1;
        const bExact = b.amount_delta_cents === 0 ? 0 : 1;
        if (aExact !== bExact) return aExact - bExact;
        const aDays = Math.abs(a.posted_delta_days);
        const bDays = Math.abs(b.posted_delta_days);
        if (aDays !== bDays) return aDays - bDays;
        return a.amount_delta_cents - b.amount_delta_cents;
      });
      return candidates;
    },
    // The picker's whole point is to be fresh — when the operator opens
    // it, they want the newest possible data. Keep cache small.
    staleTime: 30 * 1000,
  });
}

/**
 * Break a transfer pair (clears `transfer_group_id` on both halves).
 * Does NOT undo the auto-applied qb_account_id / expense_status defaults
 * — those may have been edited since the pair was stamped, and the
 * Detail page surfaces the editable controls right next to the Unlink
 * button so the operator can correct them by hand if the unlink was the
 * start of a "this wasn't really a transfer" recategorisation.
 */
export function useUnlinkTransactionFromTransfer() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: { transactionId: string }) => {
      const { error } = await supabase.rpc('unlink_transaction_from_transfer', {
        p_transaction_id: input.transactionId,
      });
      if (error) throw error;
    },
    onSuccess: (_data, vars) => {
      qc.invalidateQueries({ queryKey: ['transactions'] });
      qc.invalidateQueries({ queryKey: ['transaction', vars.transactionId] });
      qc.invalidateQueries({ queryKey: ['expense-transactions'] });
      qc.invalidateQueries({ queryKey: ['daily-cash-flow'] });
    },
  });
}

/**
 * Returns the top 1–3 historically-most-frequently-used QB categories for
 * every transaction in `transactionIds`, derived from the org's own past
 * picks for transactions with the same vendor key (lower-cased counterparty
 * or first 30 chars of description as a fallback). Backed by the
 * `suggest_qb_categories_for_transactions` RPC (see migration 27).
 *
 * One round-trip per page, not per row — the RPC takes the full id list
 * and returns at most three suggestion rows per input transaction. The
 * hook reshapes the flat response into a `Map<txId, qb_account_id[]>` so
 * the dropdown can do an O(1) lookup per row at render time.
 *
 * Returns an empty Map when there are no input ids or the user is
 * non-super_admin (RLS would return nothing anyway).
 */
export function useSuggestedQbCategoriesBatch(transactionIds: string[]) {
  const enabled = useFinancialsEnabled() && transactionIds.length > 0;
  // React Query compares queryKey contents structurally, so a freshly-
  // constructed sorted array per render still hits the cache as long as
  // the underlying ids are unchanged.
  const sortedIds = [...transactionIds].sort();
  return useQuery({
    queryKey: ['qb-category-suggestions', sortedIds],
    enabled,
    // Suggestions only change when the user assigns a new category, which
    // already invalidates this key via `useUpdateTransactionQbAccount`.
    // Keep the cache hot for the rest of the session to avoid re-fetching
    // on every dropdown click.
    staleTime: 5 * 60 * 1000,
    queryFn: async () => {
      const { data, error } = await supabase.rpc(
        'suggest_qb_categories_for_transactions',
        { p_transaction_ids: sortedIds },
      );
      if (error) throw error;
      const map = new Map<string, string[]>();
      for (const r of data ?? []) {
        const arr = map.get(r.transaction_id) ?? [];
        arr.push(r.qb_account_id);
        map.set(r.transaction_id, arr);
      }
      return map;
    },
  });
}

/**
 * Single-shot Anthropic call that recommends a QB Chart-of-Accounts
 * category for one transaction, along with a 1–2 sentence rationale. The
 * heavy lifting (loading the CoA + historic Atlas picks + pre-cutover
 * QBO ledger, building the prompt, validating Claude's pick against the
 * live CoA) lives in the `qb-category-ai-suggest` Edge Function so the
 * API key never leaves the server.
 *
 * The hook caches generously (5 minutes staleTime + 30 minutes gcTime) —
 * a category suggestion for a given row doesn't change minute-to-minute
 * and each call costs a real Anthropic token bill. Operator-driven
 * mutations that meaningfully change the inputs (the description /
 * memo / explanation overrides on the same page, picking a new vendor,
 * or assigning a different category and having that influence future
 * suggestions) invalidate the relevant detail key so the next render
 * re-fetches.
 *
 * Disabled until the caller actually opens the Transaction Detail page
 * (gated by `transactionId`) to avoid spending tokens on every row of
 * the list view.
 */
export interface AiCategorySuggestionAlternative {
  label: string;
  why_not: string;
}

export interface AiCategorySuggestion {
  qb_account_id: string | null;
  qb_account_name: string | null;
  reasoning: string;
  alternatives_considered: AiCategorySuggestionAlternative[];
  confidence: 'high' | 'medium' | 'low' | null;
}

export function useAiCategorySuggestion(transactionId: string | undefined) {
  const enabled = useFinancialsEnabled() && !!transactionId;
  return useQuery({
    queryKey: ['qb-category-ai-suggestion', transactionId],
    enabled,
    // The model output is deterministic-ish for a given row — cache for
    // the full session so navigating back and forth on the detail page
    // doesn't re-burn tokens. Mutations to the transaction's fields
    // (description / memo / explanation / vendor / category) invalidate
    // this key explicitly so an edit doesn't show a stale rationale.
    staleTime: 60 * 60 * 1000,
    gcTime: 60 * 60 * 1000,
    retry: 1,
    queryFn: async () => {
      const { data, error } = await supabase.functions.invoke<{
        ok: boolean;
        suggestion?: AiCategorySuggestion;
        model?: string;
        error?: string;
        debug?: { historic_atlas: number; historic_qbo: number; candidates: number };
      }>('qb-category-ai-suggest', {
        body: { transaction_id: transactionId },
      });
      if (error) {
        const msg = await extractFunctionErrorMessage(error);
        throw new Error(msg);
      }
      if (!data?.ok || !data.suggestion) {
        throw new Error(data?.error ?? 'AI suggestion failed');
      }
      return {
        suggestion: data.suggestion,
        model: data.model ?? null,
        debug: data.debug ?? null,
      };
    },
  });
}

/**
 * AI runway estimate that powers the full-width banner at the top of the
 * super-admin dashboard. The `finance-runway-ai` Edge Function reads the live
 * cash position + recent monthly cash trend and asks Claude the single
 * question the owner cares about: WHEN do we run out of money — or, if we're
 * profitable, HOW MUCH cash do we finish the year with?
 *
 * Cached for an hour: the answer is derived from month-granularity history and
 * a live balance, neither of which moves fast enough to justify re-burning
 * tokens on every dashboard visit within a session.
 */
export type RunwayStatus = 'burning' | 'profitable' | 'unknown';

export interface FinanceRunway {
  status: RunwayStatus;
  headline: string;
  date: string | null;
  projected_amount_cents: number | null;
  monthly_net_cents: number;
  months_of_runway: number | null;
  reasoning: string;
  confidence: 'high' | 'medium' | 'low' | null;
}

export function useFinanceRunway() {
  const enabled = useFinancialsEnabled();
  return useQuery({
    queryKey: ['finance-runway'],
    enabled,
    staleTime: 60 * 60 * 1000,
    gcTime: 60 * 60 * 1000,
    retry: 1,
    queryFn: async () => {
      const { data, error } = await supabase.functions.invoke<{
        ok: boolean;
        runway?: FinanceRunway;
        model?: string;
        error?: string;
        debug?: { months: number };
      }>('finance-runway-ai', { body: {} });
      if (error) {
        const msg = await extractFunctionErrorMessage(error);
        throw new Error(msg);
      }
      if (!data?.ok || !data.runway) {
        throw new Error(data?.error ?? 'Runway estimate failed');
      }
      return { runway: data.runway, model: data.model ?? null };
    },
  });
}

/**
 * Persist a manual QB Chart-of-Accounts assignment for a single Mercury
 * transaction. Pass `qb_account_id: null` to clear it. Invalidates every
 * cached `useTransactions(...)` query so the table re-fetches with the
 * embedded `chart_of_accounts` row updated.
 *
 * The DB-level trigger `tg_transactions_qb_status_sync` derives
 * `qb_post_status` from (qb_account_id, expense_status), so when the
 * super_admin categorises a non-employee-managed transaction (a wire / ACH
 * / transfer with no explanation or receipt) we ALSO mark it approved.
 * Without this the row would land in 'awaiting_approval' and never make it
 * to QBO. Employee-touched rows (anything with an explanation or a
 * receipt) keep the explicit approve-from-Expense-Tracking flow.
 */
export function useUpdateTransactionQbAccount() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, qb_account_id }: { id: string; qb_account_id: string | null }) => {
      // Inspect the existing row so we know whether to imply approval.
      const { data: existing, error: readErr } = await supabase
        .from('transactions')
        .select('id, expense_status, expense_explanation, receipt_path')
        .eq('id', id)
        .single();
      if (readErr) throw readErr;

      const isEmployeeTouched =
        !!existing?.expense_explanation || !!existing?.receipt_path;

      const update: { qb_account_id: string | null; expense_status?: 'approved' } = {
        qb_account_id,
      };
      if (
        qb_account_id &&
        existing?.expense_status === 'draft' &&
        !isEmployeeTouched
      ) {
        update.expense_status = 'approved';
      }

      const { data, error } = await supabase
        .from('transactions')
        .update(update)
        .eq('id', id)
        .select('id, qb_account_id, qb_post_status, expense_status')
        .single();
      if (error) throw error;
      return data;
    },
    onSuccess: (_data, variables) => {
      qc.invalidateQueries({ queryKey: ['transactions'] });
      qc.invalidateQueries({ queryKey: ['transaction', variables.id] });
      qc.invalidateQueries({ queryKey: ['expense-transactions'] });
      qc.invalidateQueries({ queryKey: ['expense-transaction', variables.id] });
      // A fresh assignment changes what we'd suggest for every future
      // transaction with the same vendor key. Invalidate the suggestion
      // cache so the next dropdown open reflects this pick.
      qc.invalidateQueries({ queryKey: ['qb-category-suggestions'] });
    },
  });
}

/**
 * POSTs the selected Atlas transaction(s) to QuickBooks as Purchase records
 * via the qbo-post-transaction Edge Function. The function is idempotent on
 * `qb_external_id` so calling this for an already-posted transaction issues
 * an UPDATE in QBO instead of creating a duplicate.
 *
 * Pass `transaction_ids: undefined` (or an empty array) to post EVERY
 * transaction whose status is 'pending' — that's the "Post all categorized"
 * affordance on the Transactions page header.
 */
export function usePostTransactionsToQbo() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: { transaction_ids?: string[] }) => {
      const { data, error } = await supabase.functions.invoke<{
        ok: boolean;
        posted: number;
        failed: number;
        results: Array<{
          id: string;
          status: 'posted' | 'failed' | 'skipped';
          error?: string;
          // True when the existing QBO entity (Purchase/Deposit/Transfer)
          // had been deleted/voided in QBO since the original post, so
          // the edge function fell back to creating a fresh one instead
          // of updating in place. The UI uses this to swap the toast
          // wording.
          recreated?: boolean;
          // Outcome of mirroring the Atlas receipt into QBO as an
          // Attachable. The post itself is `posted` regardless — this is
          // a separate, non-fatal signal. The UI shows a warning toast
          // when this is 'failed' and a positive confirmation when it's
          // 'uploaded' / 'replaced' so the operator knows the receipt
          // followed the row across.
          attachment?:
            | 'uploaded'
            | 'replaced'
            | 'removed'
            | 'unchanged'
            | 'none'
            | 'failed';
          attachment_error?: string;
        }>;
        error?: string;
      }>('qbo-post-transaction', {
        body: input.transaction_ids?.length
          ? { transaction_ids: input.transaction_ids }
          : {},
      });
      if (error) {
        const msg = await extractFunctionErrorMessage(error);
        throw new Error(msg);
      }
      if (!data?.ok) throw new Error(data?.error ?? 'Post failed');
      return data;
    },
    // Optimistic update: flip the targeted rows to 'posting' immediately
    // so the UI reflects the click before the round-trip completes. The
    // server-side update from qbo-post-transaction will overwrite this
    // (with 'posted' on success or 'failed' on failure) once the awaited
    // invalidation below refetches. Without this, the row sits at
    // 'pending' for ~1–3s after the user clicks Post, making it look
    // like nothing happened.
    onMutate: async (variables) => {
      const ids = variables.transaction_ids ?? null;
      await qc.cancelQueries({ queryKey: ['transactions'] });
      await qc.cancelQueries({ queryKey: ['transaction'] });

      const previousLists = qc.getQueriesData<unknown[]>({ queryKey: ['transactions'] });
      for (const [key, list] of previousLists) {
        if (!Array.isArray(list)) continue;
        qc.setQueryData(
          key,
          list.map((row) => {
            const r = row as Record<string, unknown>;
            const matches = ids ? ids.includes(r.id as string) : r.qb_post_status === 'pending';
            if (!matches) return row;
            return { ...r, qb_post_status: 'posting' };
          }),
        );
      }

      // Single-transaction detail view (if open).
      if (ids) {
        for (const id of ids) {
          const detailKey = ['transaction', id] as const;
          const prev = qc.getQueryData<Record<string, unknown>>(detailKey);
          if (prev) {
            qc.setQueryData(detailKey, { ...prev, qb_post_status: 'posting' });
          }
        }
      }

      return { previousLists };
    },
    onError: (_err, _vars, context) => {
      // Roll back the optimistic flip so the row goes back to whatever it
      // was. The awaited invalidation in onSettled would also reconcile
      // eventually, but rolling back gives an instant correct UI.
      if (context?.previousLists) {
        for (const [key, data] of context.previousLists) {
          qc.setQueryData(key, data);
        }
      }
    },
    onSettled: async (_data, _err, variables) => {
      // Await every invalidation so `mutateAsync` only resolves after the
      // tables, detail views, and expense queues have re-fetched. The
      // toast and the row update then land in the same render and the
      // "Post N pending" counter decrements without the user having to
      // wait or refresh.
      await Promise.all([
        qc.invalidateQueries({ queryKey: ['transactions'] }),
        qc.invalidateQueries({ queryKey: ['transaction'] }),
        qc.invalidateQueries({ queryKey: ['expense-transactions'] }),
        variables.transaction_ids?.length
          ? Promise.all(
              variables.transaction_ids.map((id) =>
                qc.invalidateQueries({ queryKey: ['expense-transaction', id] }),
              ),
            )
          : qc.invalidateQueries({ queryKey: ['expense-transaction'] }),
      ]);
    },
  });
}

/**
 * Read-only mirror of the pre-cutover (pre-2026-05-01) QuickBooks ledger.
 * Populated by the `qbo-history-sync` Edge Function — one row per
 * QBO TransactionList posting line. Surfaced as the "History" tab on
 * /financials/transactions.
 *
 * Returns rows ordered newest-first so the most recent reconciled period
 * shows at the top of the table; the History tab applies all filtering
 * and pagination client-side via the DataTable component.
 */
export function useQboHistoricalTransactions() {
  const enabled = useFinancialsEnabled();
  return useQuery({
    queryKey: ['qbo-historical-transactions'],
    enabled,
    queryFn: async () => {
      const { data, error } = await supabase
        .from('qbo_historical_transactions')
        .select('*')
        .order('posted_at', { ascending: false })
        .limit(5000);
      if (error) throw error;
      return (data ?? []) as Array<Tables<'qbo_historical_transactions'>>;
    },
  });
}

/**
 * Diagnostic payload returned by qbo-history-sync alongside the row count.
 * Captured so the History tab can show "0 rows pulled — here's what QBO
 * returned" without making the operator scrape the Edge Function logs.
 */
export interface QboHistorySyncDiagnostics {
  header: Record<string, unknown> | null;
  column_count: number;
  columns: Array<{
    ColTitle: string | null;
    ColType: string | null;
    ColKey: string | null;
  }>;
  raw_data_row_count: number;
  sample_row: Array<{ value?: string; id?: string }> | null;
  /**
   * Results of the secondary `is_cleared` call we make alongside the
   * main TransactionList report. `txn_ids_mapped` counts distinct QBO
   * transaction Ids for which we got a cleared status — should match
   * the unique transaction count in the main report. If `ok` is false,
   * QBO rejected the targeted call (UI shows a warning hint).
   */
  cleared_call?: {
    ok: boolean;
    txn_ids_mapped: number;
    error: string | null;
  };
}

/**
 * Invokes the `qbo-history-sync` Edge Function. The function wipes the
 * caller's `qbo_historical_transactions` rows and reloads them from QBO's
 * TransactionList report for the configured window (defaults inside the
 * function: 2025-05-01 → 2026-04-30). Manual trigger only — there is no
 * cron entry for history sync.
 */
export function useSyncQboHistory() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input?: { start_date?: string; end_date?: string }) => {
      const { data, error } = await supabase.functions.invoke<{
        ok: boolean;
        rows?: number;
        window?: { start_date: string; end_date: string };
        diagnostics?: QboHistorySyncDiagnostics;
        error?: string;
      }>('qbo-history-sync', { body: input ?? {} });
      if (error) {
        const msg = await extractFunctionErrorMessage(error);
        throw new Error(msg);
      }
      if (!data?.ok) throw new Error(data?.error ?? 'History sync failed');
      return data;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['qbo-historical-transactions'] });
    },
  });
}

/**
 * Single-row variant of useQboHistoricalTransactions. Used by the History
 * detail page (`/financials/transactions/history/:id`). Returns the row
 * plus every other historical row sharing the same QBO transaction Id —
 * those are the sibling posting lines that, together, make up the
 * "transaction" as a unit in QBO. They share Date / Payee / Doc # and
 * differ in (account, split, amount, posting_type), which is exactly the
 * data the detail page renders as a split table.
 */
export function useQboHistoricalTransaction(id: string | undefined) {
  const enabled = useFinancialsEnabled() && !!id;
  return useQuery({
    queryKey: ['qbo-historical-transaction', id],
    enabled,
    queryFn: async () => {
      const { data: row, error } = await supabase
        .from('qbo_historical_transactions')
        .select('*')
        .eq('id', id!)
        .single();
      if (error) throw error;

      // Pull sibling posting lines (same qbo_txn_id). Skip if the txn_id
      // is null on the primary row — older rows that predate the
      // qbo_txn_id capture won't have one, and `.eq(null)` doesn't do
      // what callers expect.
      let siblings: Array<Tables<'qbo_historical_transactions'>> = [];
      if (row.qbo_txn_id) {
        const { data: sibs, error: sibErr } = await supabase
          .from('qbo_historical_transactions')
          .select('*')
          .eq('qbo_txn_id', row.qbo_txn_id)
          .order('amount_cents', { ascending: false });
        if (sibErr) throw sibErr;
        siblings = (sibs ?? []) as Array<Tables<'qbo_historical_transactions'>>;
      } else {
        siblings = [row as Tables<'qbo_historical_transactions'>];
      }

      // Receipts attached to this transaction (or any of its siblings —
      // attachments link at the transaction level, not the posting line,
      // so they're identical across siblings).
      let attachments: Array<Tables<'qbo_historical_attachments'>> = [];
      if (row.qbo_txn_id) {
        const { data: atts, error: attErr } = await supabase
          .from('qbo_historical_attachments')
          .select('*')
          .eq('qbo_txn_id', row.qbo_txn_id);
        if (attErr) throw attErr;
        // Dedupe by attachable_id — a single Attachable referenced from
        // multiple AttachableRefs would otherwise show up twice.
        const seen = new Set<string>();
        attachments = ((atts ?? []) as Array<Tables<'qbo_historical_attachments'>>).filter(
          (a) => {
            if (seen.has(a.qbo_attachable_id)) return false;
            seen.add(a.qbo_attachable_id);
            return true;
          },
        );
      }

      return {
        row: row as Tables<'qbo_historical_transactions'>,
        siblings,
        attachments,
      };
    },
  });
}

/**
 * Read-only mirror of QBO Attachable rows linked to a transaction we've
 * already pulled into qbo_historical_transactions. Returned grouped by
 * qbo_txn_id so the History tab can render a Receipt column with O(1)
 * lookups per row.
 *
 * One Attachable can be linked to multiple transactions, so a single
 * row's array may contain duplicates with different qbo_txn_type — that's
 * fine, the UI just shows a paperclip per link.
 */
export function useQboHistoricalAttachments() {
  const enabled = useFinancialsEnabled();
  return useQuery({
    queryKey: ['qbo-historical-attachments'],
    enabled,
    queryFn: async () => {
      const { data, error } = await supabase
        .from('qbo_historical_attachments')
        .select('*')
        .order('synced_at', { ascending: false })
        .limit(10000);
      if (error) throw error;
      const rows = (data ?? []) as Array<Tables<'qbo_historical_attachments'>>;
      // Group by qbo_txn_id for cheap lookups in the table cell.
      const byTxnId = new Map<string, Array<Tables<'qbo_historical_attachments'>>>();
      for (const r of rows) {
        const list = byTxnId.get(r.qbo_txn_id) ?? [];
        list.push(r);
        byTxnId.set(r.qbo_txn_id, list);
      }
      return { rows, byTxnId };
    },
  });
}

/**
 * Diagnostic payload returned by qbo-history-attachments-sync. Exposed to
 * the History tab so the operator can see at a glance how many QBO
 * Attachables we examined, matched, and downloaded — and whether we hit
 * the per-run download budget.
 */
export interface QboHistoryAttachmentsSyncResult {
  attachables_examined: number;
  attachables_matched?: number;
  new_links: number;
  downloads: number;
  /**
   * Count of already-cached attachables whose bytes we re-fetched and
   * overwrote in Storage. Only non-zero when invoked with
   * `redownload_existing: true`. Used to repair corrupt blobs left
   * behind by the pre-fix version of the Edge Function (which uploaded
   * the QBO temporary-URL string instead of the actual file bytes).
   */
  redownloaded_existing?: number;
  skipped_existing: number;
  budget_hit: boolean;
  failures?: Array<{ attachable_id: string; reason: string }>;
  total_failures?: number;
  message?: string;
}

/**
 * Invokes the `qbo-history-attachments-sync` Edge Function. The function
 * paginates all QBO Attachables, filters to ones linked to a transaction
 * already in qbo_historical_transactions, downloads the file bytes, and
 * mirrors them into the qbo-historical-receipts Storage bucket plus the
 * qbo_historical_attachments table.
 *
 * Idempotent: a re-run skips attachables already cached for this org —
 * unless invoked with `{ redownload_existing: true }`, which re-fetches
 * bytes for every matched Attachable and overwrites the cached file in
 * Storage. The repair flag is meant for the one-time cleanup of corrupt
 * blobs uploaded by the pre-fix version of this function (which wrote
 * the QBO temporary download URL as the file contents).
 *
 * Has a per-run download budget (currently 200 files); if hit, the
 * response includes `budget_hit: true` and a "click again to continue"
 * message — re-invoking picks up where we left off.
 */
export function useSyncQboHistoryAttachments() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (
      args: { redownloadExisting?: boolean } | undefined = undefined,
    ) => {
      const { data, error } = await supabase.functions.invoke<
        QboHistoryAttachmentsSyncResult & { ok: boolean; error?: string }
      >('qbo-history-attachments-sync', {
        body: { redownload_existing: args?.redownloadExisting === true },
      });
      if (error) {
        const msg = await extractFunctionErrorMessage(error);
        throw new Error(msg);
      }
      if (!data?.ok) throw new Error(data?.error ?? 'Attachments sync failed');
      return data;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['qbo-historical-attachments'] });
    },
  });
}

/**
 * Mints a short-lived signed URL for a file in the qbo-historical-receipts
 * Storage bucket. The bucket is private and the read RLS policy gates on
 * (super_admin × same org), so the caller's JWT must satisfy both — which
 * is also the gate for seeing the History tab in the first place.
 *
 * Returns null if the path is empty (defensive — the caller should bail
 * before invoking).
 */
export async function getQboHistoricalReceiptUrl(
  storagePath: string,
): Promise<string | null> {
  if (!storagePath) return null;
  const { data, error } = await supabase.storage
    .from('qbo-historical-receipts')
    // 5 minutes is enough for a click-to-preview interaction without
    // leaving an unnecessarily long-lived URL floating in the browser.
    .createSignedUrl(storagePath, 300);
  if (error || !data?.signedUrl) return null;
  return data.signedUrl;
}

/**
 * Updates the per-bank-account → QBO bank/credit-card account mapping. Used
 * by the Accounts page "where does this post" picker. Required before any
 * transaction on this account can be posted to QBO.
 */
export function useUpdateBankAccountQbMapping() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, qb_account_id }: { id: string; qb_account_id: string | null }) => {
      const { data, error } = await supabase
        .from('bank_accounts')
        .update({ qb_account_id })
        .eq('id', id)
        .select('id, qb_account_id')
        .single();
      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['bank-accounts'] });
      // Posting requires this mapping; refresh the transactions list so the
      // status pills re-evaluate (a missing mapping shows as "needs mapping").
      qc.invalidateQueries({ queryKey: ['transactions'] });
    },
  });
}

export async function extractFunctionErrorMessage(error: unknown): Promise<string> {
  const fallback = error instanceof Error ? error.message : 'Edge Function call failed';
  const ctx = (error as FunctionsHttpError).context as Response | undefined;
  if (!ctx || typeof ctx.json !== 'function') return fallback;
  try {
    const cloned = ctx.clone();
    const body = (await cloned.json()) as { error?: string };
    return body?.error ?? fallback;
  } catch {
    return fallback;
  }
}

export interface MonthlyPnl {
  month: string;
  account_type: 'revenue' | 'expense';
  account_id: string;
  account_name: string;
  amount_cents: number;
}

export function useMonthlyPnl() {
  const enabled = useFinancialsEnabled();
  return useQuery({
    queryKey: ['monthly-pnl'],
    enabled,
    queryFn: async () => {
      const { data, error } = await supabase
        .from('v_monthly_pnl' as never)
        .select('*')
        .order('month', { ascending: true });
      if (error) throw error;
      return data as unknown as MonthlyPnl[];
    },
  });
}

export interface BalanceSheetRow {
  account_type: 'asset' | 'liability' | 'equity';
  account_id: string;
  account_name: string;
  balance_cents: number;
}

export function useBalanceSheet() {
  const enabled = useFinancialsEnabled();
  return useQuery({
    queryKey: ['balance-sheet'],
    enabled,
    queryFn: async () => {
      const { data, error } = await supabase
        .from('v_balance_sheet' as never)
        .select('*');
      if (error) throw error;
      return data as unknown as BalanceSheetRow[];
    },
  });
}

/**
 * End-of-month balance per (account, month). Sibling to `useMonthlyPnl()`
 * but reads `qbo_balance_monthly` directly so the BalanceSheet tab can
 * render a per-month matrix the same way the P&L does. Unlike P&L cells
 * (which are PERIOD totals) these are point-in-time balances, so the UI
 * must NOT sum them across columns — each column is a self-contained
 * snapshot as of that month-end.
 *
 * Note: we read the table rather than `v_balance_sheet` because the view
 * collapses to a single latest snapshot per org, which is exactly what
 * we want to avoid for a per-month matrix.
 */
export interface MonthlyBalanceRow {
  month: string;
  account_id: string;
  balance_cents: number;
}

export function useMonthlyBalanceSheet() {
  const enabled = useFinancialsEnabled();
  return useQuery({
    queryKey: ['monthly-balance-sheet'],
    enabled,
    queryFn: async () => {
      const { data, error } = await supabase
        .from('qbo_balance_monthly' as never)
        .select('account_id, month, balance_cents')
        .order('month', { ascending: true });
      if (error) throw error;
      return data as unknown as MonthlyBalanceRow[];
    },
  });
}

/**
 * Lean, full-history transaction feed for the Actuals → Cash flow register.
 *
 * Unlike `useTransactions` this:
 *   • selects only the columns the register needs (no card / CoA / payee
 *     joins) so pulling thousands of rows stays cheap, and
 *   • does NOT collapse internal transfers — both legs matter. A
 *     checking→savings transfer nets to zero across the deposit total only
 *     if BOTH the debit (out of checking) and credit (into savings) rows
 *     are present; dropping the follower would make the running cash
 *     balance drift.
 *
 * Ordered newest-first from the DB; the register re-sorts ascending
 * client-side to walk the running balances. `limit` defaults high because
 * the register shows the whole history, not a rolling window.
 */
export interface CashRegisterTxn {
  id: string;
  posted_at: string;
  description: string;
  description_override: string | null;
  amount_cents: number;
  direction: 'debit' | 'credit';
  bank_account_id: string;
  /**
   * Non-null when this row is one leg of a paired internal transfer (e.g.
   * checking → savings, or a credit-card payment out of checking). The
   * register uses it to drop transfers out of the Money in / Money out
   * stats — they shuffle cash between our own accounts rather than
   * representing external income or spend.
   */
  transfer_group_id: string | null;
  bank_accounts: Pick<Tables<'bank_accounts'>, 'name' | 'account_type'> | null;
}

export function useCashRegisterTransactions(limit = 10000) {
  const enabled = useFinancialsEnabled();
  return useQuery({
    queryKey: ['cash-register-transactions', limit],
    enabled,
    queryFn: async () => {
      const { data, error } = await supabase
        .from('transactions')
        .select(
          'id, posted_at, description, description_override, amount_cents, ' +
            'direction, bank_account_id, transfer_group_id, bank_accounts(name, account_type)',
        )
        .order('posted_at', { ascending: false })
        .limit(limit);
      if (error) throw error;
      return data as unknown as CashRegisterTxn[];
    },
  });
}

export interface DailyCashFlowRow {
  day: string;
  direction: 'debit' | 'credit';
  amount_cents: number;
  txn_count: number;
}

export function useDailyCashFlow(params?: { days?: number }) {
  const enabled = useFinancialsEnabled();
  return useQuery({
    queryKey: ['daily-cash-flow', params],
    enabled,
    queryFn: async () => {
      const since = new Date(Date.now() - (params?.days ?? 60) * 24 * 60 * 60 * 1000).toISOString();
      const { data, error } = await supabase
        .from('v_daily_cash_flow' as never)
        .select('*')
        .gte('day', since)
        .order('day');
      if (error) throw error;
      return data as unknown as DailyCashFlowRow[];
    },
  });
}

/**
 * The QBO Chart of Accounts is readable by every authenticated org member
 * (see migration 21 — it's a list of category names, no balance data), so
 * this hook gates on profile presence rather than the super_admin-only
 * financials module. The Expense Tracking pages need CoA to populate the
 * Category dropdown for any role.
 */
export function useChartOfAccounts() {
  const { profile, loading } = useAuth();
  const enabled = !loading && !!profile;
  return useQuery({
    queryKey: ['chart-of-accounts'],
    enabled,
    queryFn: async () => {
      const { data, error } = await supabase
        .from('chart_of_accounts')
        .select('*')
        .order('code');
      if (error) throw error;
      return data as Tables<'chart_of_accounts'>[];
    },
  });
}

export function useVendors() {
  const enabled = useFinancialsEnabled();
  return useQuery({
    queryKey: ['vendors'],
    enabled,
    queryFn: async () => {
      const { data, error } = await supabase
        .from('vendors')
        .select('*')
        .order('name');
      if (error) throw error;
      return data as Tables<'vendors'>[];
    },
  });
}

export function useCustomers() {
  const enabled = useFinancialsEnabled();
  return useQuery({
    queryKey: ['customers'],
    enabled,
    queryFn: async () => {
      const { data, error } = await supabase
        .from('customers')
        .select('*')
        .order('name');
      if (error) throw error;
      return data as Tables<'customers'>[];
    },
  });
}

/**
 * AR / AP aging — one row per counterparty (customer for AR, vendor for
 * AP) with open balances bucketed by days-past-due. Source: migration 73
 * views `v_ar_aging` / `v_ap_aging`, which read the document-level
 * `qbo_invoices` / `qbo_bills` tables maintained by `qbo-sync`.
 *
 * Aging convention (mirrors QuickBooks A/R + A/P Aging Summary reports):
 *   not_due       — due date is in the future
 *   bucket_0_30   — 0–30 days past due
 *   bucket_31_60  — 31–60 days past due
 *   bucket_61_90  — 61–90 days past due
 *   bucket_over_90 — > 90 days past due
 * Counterparties with no open balance are absent. All amounts in cents.
 */
export interface ArAgingRow {
  organization_id: string;
  customer_id: string | null;
  qb_customer_id: string | null;
  customer_name: string | null;
  not_due_cents: number;
  bucket_0_30_cents: number;
  bucket_31_60_cents: number;
  bucket_61_90_cents: number;
  bucket_over_90_cents: number;
  total_open_cents: number;
  invoice_count: number;
  oldest_due_date: string | null;
  max_days_past_due: number | null;
}

export function useArAging() {
  const enabled = useFinancialsEnabled();
  return useQuery({
    queryKey: ['ar-aging'],
    enabled,
    queryFn: async () => {
      const { data, error } = await supabase
        .from('v_ar_aging' as never)
        .select('*')
        .order('total_open_cents', { ascending: false });
      if (error) throw error;
      return data as unknown as ArAgingRow[];
    },
  });
}

export interface ApAgingRow {
  organization_id: string;
  vendor_id: string | null;
  qb_vendor_id: string | null;
  vendor_name: string | null;
  not_due_cents: number;
  bucket_0_30_cents: number;
  bucket_31_60_cents: number;
  bucket_61_90_cents: number;
  bucket_over_90_cents: number;
  total_open_cents: number;
  bill_count: number;
  oldest_due_date: string | null;
  max_days_past_due: number | null;
}

export function useApAging() {
  const enabled = useFinancialsEnabled();
  return useQuery({
    queryKey: ['ap-aging'],
    enabled,
    queryFn: async () => {
      const { data, error } = await supabase
        .from('v_ap_aging' as never)
        .select('*')
        .order('total_open_cents', { ascending: false });
      if (error) throw error;
      return data as unknown as ApAgingRow[];
    },
  });
}

/**
 * Flat per-invoice / per-bill view for the future drill-down on the AR/AP
 * aging tables. Each row carries the pre-computed `aging_bucket` label and
 * `days_past_due` integer so the drawer can render "INV-1042 · 47 days
 * past due · $1,200 open" without recomputing on the client.
 */
export interface ArAgingInvoiceRow {
  invoice_id: string;
  organization_id: string;
  customer_id: string | null;
  qb_customer_id: string | null;
  customer_name: string | null;
  qb_external_id: string;
  doc_number: string | null;
  txn_date: string;
  due_date: string | null;
  effective_due_date: string;
  days_past_due: number;
  aging_bucket: 'not_due' | '0_30' | '31_60' | '61_90' | 'over_90';
  total_cents: number;
  balance_cents: number;
  currency: string | null;
  memo: string | null;
  synced_at: string;
}

export function useArAgingInvoices(params?: { customerId?: string | null }) {
  const enabled = useFinancialsEnabled();
  return useQuery({
    queryKey: ['ar-aging-invoices', params],
    enabled,
    queryFn: async () => {
      let q = supabase
        .from('v_ar_aging_invoices' as never)
        .select('*')
        .order('days_past_due', { ascending: false });
      if (params?.customerId) {
        q = q.eq('customer_id', params.customerId);
      }
      const { data, error } = await q;
      if (error) throw error;
      return data as unknown as ArAgingInvoiceRow[];
    },
  });
}

export interface ApAgingBillRow {
  bill_id: string;
  organization_id: string;
  vendor_id: string | null;
  qb_vendor_id: string | null;
  vendor_name: string | null;
  qb_external_id: string;
  doc_number: string | null;
  txn_date: string;
  due_date: string | null;
  effective_due_date: string;
  days_past_due: number;
  aging_bucket: 'not_due' | '0_30' | '31_60' | '61_90' | 'over_90';
  total_cents: number;
  balance_cents: number;
  currency: string | null;
  memo: string | null;
  synced_at: string;
}

export function useApAgingBills(params?: { vendorId?: string | null }) {
  const enabled = useFinancialsEnabled();
  return useQuery({
    queryKey: ['ap-aging-bills', params],
    enabled,
    queryFn: async () => {
      let q = supabase
        .from('v_ap_aging_bills' as never)
        .select('*')
        .order('days_past_due', { ascending: false });
      if (params?.vendorId) {
        q = q.eq('vendor_id', params.vendorId);
      }
      const { data, error } = await q;
      if (error) throw error;
      return data as unknown as ApAgingBillRow[];
    },
  });
}

/**
 * Combined picker source for the Transactions page "Name" dropdown. The
 * QBO `EntityRef` on a Purchase can be a Vendor or a Customer — almost
 * always a Vendor for debits and a Customer for the rare credit refund —
 * so the picker shows both lists in one place. We surface only entities
 * that have a `qb_external_id` (i.e. mirrored from QBO by qbo-sync); a
 * vendor created by hand in Atlas with no QBO id isn't useful as an
 * EntityRef target and would fail at post time.
 *
 * Returned as a flat array with a `type` discriminator so the picker can
 * group + filter without a second hook. Sorted by name so the dropdown
 * scans cleanly when there are many rows.
 */
export type QbEntityPick = {
  type: 'vendor' | 'customer';
  id: string;
  name: string;
  qb_external_id: string;
};

export function useQbEntities() {
  const enabled = useFinancialsEnabled();
  return useQuery({
    queryKey: ['qb-entities'],
    enabled,
    queryFn: async () => {
      const [vRes, cRes] = await Promise.all([
        supabase
          .from('vendors')
          .select('id, name, qb_external_id, is_active')
          .not('qb_external_id', 'is', null)
          .eq('is_active', true)
          .order('name'),
        supabase
          .from('customers')
          .select('id, name, qb_external_id, is_active')
          .not('qb_external_id', 'is', null)
          .eq('is_active', true)
          .order('name'),
      ]);
      if (vRes.error) throw vRes.error;
      if (cRes.error) throw cRes.error;
      const vendors: QbEntityPick[] = (vRes.data ?? []).map((v) => ({
        type: 'vendor',
        id: v.id,
        name: v.name,
        qb_external_id: v.qb_external_id as string,
      }));
      const customers: QbEntityPick[] = (cRes.data ?? []).map((c) => ({
        type: 'customer',
        id: c.id,
        name: c.name,
        qb_external_id: c.qb_external_id as string,
      }));
      return { vendors, customers };
    },
  });
}

/**
 * Persist the picked QBO payee on a transaction. The `transactions_one_qb_payee`
 * check constraint requires that at most one of (qb_vendor_id, qb_customer_id)
 * is set, so this mutation always writes both columns — the unused one is
 * cleared to null. Passing `entity: null` clears the picker entirely (the
 * row's EntityRef falls back to the legacy ILIKE lookup on counterparty,
 * which usually returns nothing — i.e. the next Purchase will post with a
 * blank Name in QBO, deliberately).
 */
export function useUpdateTransactionEntity() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({
      id,
      entity,
    }: {
      id: string;
      entity: { type: 'vendor' | 'customer'; id: string } | null;
    }) => {
      const update: {
        qb_vendor_id: string | null;
        qb_customer_id: string | null;
      } = { qb_vendor_id: null, qb_customer_id: null };
      if (entity?.type === 'vendor') update.qb_vendor_id = entity.id;
      if (entity?.type === 'customer') update.qb_customer_id = entity.id;
      const { data, error } = await supabase
        .from('transactions')
        .update(update)
        .eq('id', id)
        .select('id, qb_vendor_id, qb_customer_id')
        .single();
      if (error) throw error;
      return data;
    },
    onSuccess: (_data, variables) => {
      qc.invalidateQueries({ queryKey: ['transactions'] });
      qc.invalidateQueries({ queryKey: ['transaction', variables.id] });
      qc.invalidateQueries({ queryKey: ['expense-transactions'] });
      qc.invalidateQueries({ queryKey: ['expense-transaction', variables.id] });
      // Vendor / customer is a strong signal for the AI category picker
      // (more so than the bank-feed counterparty), so a change here should
      // re-run the suggestion the next time the panel opens.
      qc.invalidateQueries({ queryKey: ['qb-category-ai-suggestion', variables.id] });
    },
  });
}

/**
 * Persist the operator's edited description / memo on a single transaction.
 *
 * Both fields are *override* columns (see migration 66): NULL means "fall
 * back to the bank-feed value", which is what the UI's "Reset" button does.
 * The bank-feed columns themselves (`description`, `memo`) are read-only
 * from the client — mercury-sync / plaid-sync own them.
 *
 * Pass `undefined` for either field to leave it unchanged; pass `null` to
 * explicitly clear an override. The mutation issues a single UPDATE so a
 * combined save (e.g. "edited both fields then clicked save") never lands
 * a partial row.
 */
export function useUpdateTransactionTextFields() {  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({
      id,
      description_override,
      memo_override,
    }: {
      id: string;
      description_override?: string | null;
      memo_override?: string | null;
    }) => {
      const update: { description_override?: string | null; memo_override?: string | null } = {};
      if (description_override !== undefined) update.description_override = description_override;
      if (memo_override !== undefined) update.memo_override = memo_override;
      if (Object.keys(update).length === 0) {
        // Defensive — caller passed neither field. Avoid hitting Supabase
        // with an empty UPDATE (which PostgREST rejects).
        return { id };
      }
      const { data, error } = await supabase
        .from('transactions')
        .update(update)
        .eq('id', id)
        .select('id, description_override, memo_override')
        .single();
      if (error) throw error;
      return data;
    },
    onSuccess: (_data, variables) => {
      qc.invalidateQueries({ queryKey: ['transactions'] });
      qc.invalidateQueries({ queryKey: ['transaction', variables.id] });
      qc.invalidateQueries({ queryKey: ['expense-transactions'] });
      qc.invalidateQueries({ queryKey: ['expense-transaction', variables.id] });
      // Description / memo are first-class inputs to the AI categoriser.
      // Drop the cached suggestion so the panel re-fetches with the new
      // text the next time the operator looks at it.
      qc.invalidateQueries({ queryKey: ['qb-category-ai-suggestion', variables.id] });
    },
  });
}

/**
 * Read every `transaction_splits` row for a transaction, joined to the
 * assigned chart_of_accounts row so the editor can render labels without
 * a second roundtrip. Returns rows ordered by `line_number` so the
 * editor preserves the operator's order across saves.
 *
 * Empty array means "this transaction is single-category" — the
 * common case. The detail page uses `splits.length === 0` to decide
 * between the single-category picker and the splits editor.
 */
export type TransactionSplit = Tables<'transaction_splits'> & {
  chart_of_accounts: Pick<
    Tables<'chart_of_accounts'>,
    'id' | 'name' | 'account_type' | 'subtype' | 'code'
  > | null;
};

export function useTransactionSplits(transactionId: string | undefined) {
  const enabled = useFinancialsEnabled() && !!transactionId;
  return useQuery({
    queryKey: ['transaction-splits', transactionId],
    enabled,
    queryFn: async (): Promise<TransactionSplit[]> => {
      const { data, error } = await supabase
        .from('transaction_splits')
        .select(
          'id, transaction_id, organization_id, line_number, qb_account_id, ' +
            'amount_cents, description, created_at, updated_at, ' +
            'chart_of_accounts:qb_account_id(id, name, account_type, subtype, code)',
        )
        .eq('transaction_id', transactionId!)
        .order('line_number', { ascending: true });
      if (error) throw error;
      return (data ?? []) as unknown as TransactionSplit[];
    },
  });
}

/**
 * Atomically replace every split for a transaction. Pass an empty array
 * to clear the splits and revert to the single-category shape. Goes
 * through the `set_transaction_splits` RPC so:
 *   • amounts are validated to sum exactly to the parent's amount_cents
 *   • each qb_account_id is checked against chart_of_accounts (active,
 *     same org)
 *   • the parent row's `qb_post_status` is recomputed via the
 *     status-sync trigger (a row that gains splits transitions from
 *     'unassigned' → 'pending' / 'awaiting_approval' the moment the
 *     RPC commits, even if the parent's qb_account_id is null).
 *
 * Caller side passes plain numbers / strings for amounts and account
 * ids; we forward as-is — Postgres parses `amount_cents` from the
 * stringified JSON number on the server. A failed RPC throws with
 * Postgres's error message ("Splits sum to X cents but the transaction
 * is Y cents …") so the toast shows the operator the actual problem.
 */
export interface TransactionSplitInput {
  qb_account_id: string;
  amount_cents: number;
  description?: string | null;
}

export function useUpdateTransactionSplits() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: {
      transactionId: string;
      splits: TransactionSplitInput[];
    }) => {
      const { error } = await supabase.rpc('set_transaction_splits', {
        p_transaction_id: input.transactionId,
        // The RPC arg is typed as `Json` in our generated stub; the
        // runtime shape is an array of plain objects with primitive
        // fields, which `Json` allows. We launder the array through
        // `Json` directly — TS can't structurally widen an
        // interface-typed array to the recursive Json union without
        // help, but the runtime values fit fine.
        p_splits: input.splits as unknown as Json,
      });
      if (error) throw error;
    },
    onSuccess: (_data, vars) => {
      qc.invalidateQueries({ queryKey: ['transaction-splits', vars.transactionId] });
      qc.invalidateQueries({ queryKey: ['transactions'] });
      qc.invalidateQueries({ queryKey: ['transaction', vars.transactionId] });
      qc.invalidateQueries({ queryKey: ['expense-transactions'] });
      qc.invalidateQueries({ queryKey: ['expense-transaction', vars.transactionId] });
      // A new split set changes the per-line categorisations the AI
      // categoriser sees; drop its cache so the next visit re-runs.
      qc.invalidateQueries({ queryKey: ['qb-category-ai-suggestion', vars.transactionId] });
      qc.invalidateQueries({ queryKey: ['qb-category-suggestions'] });
    },
  });
}

/**
 * Cross-transaction split summary — for every transaction id passed in,
 * returns the count of splits and (when one exists) the first split's
 * category name. Used by the Live Transactions list to render
 * "(N splits)" / "Split: Office Supplies +2 more" in the QB Category
 * column without one query per row.
 *
 * Implemented as a single PostgREST select rather than an RPC because
 * the join cost is small (RLS-filtered to the operator's org, indexed
 * on `transaction_id`). Returns a Map keyed by transaction_id so the
 * column renderer can do an O(1) lookup at render time. Empty Map
 * when there are no input ids.
 */
export interface TransactionSplitSummary {
  count: number;
  firstCategoryName: string | null;
  totalCents: number;
}

export function useTransactionSplitsSummary(transactionIds: string[]) {
  const enabled = useFinancialsEnabled() && transactionIds.length > 0;
  // Stable cache key: the underlying data only changes when an
  // operator edits a split, which already invalidates this key via
  // useUpdateTransactionSplits. Sort the ids so renders that pass the
  // same set in different orders still hit the cache.
  const sortedIds = [...transactionIds].sort();
  return useQuery({
    queryKey: ['transaction-splits-summary', sortedIds],
    enabled,
    staleTime: 60 * 1000,
    queryFn: async () => {
      const { data, error } = await supabase
        .from('transaction_splits')
        .select(
          'id, transaction_id, line_number, amount_cents, ' +
            'chart_of_accounts:qb_account_id(name)',
        )
        .in('transaction_id', sortedIds);
      if (error) throw error;
      type Row = {
        id: string;
        transaction_id: string;
        line_number: number;
        amount_cents: number;
        chart_of_accounts: { name: string } | null;
      };
      const rows = (data ?? []) as unknown as Row[];
      const map = new Map<string, TransactionSplitSummary>();
      // Sort by line_number ascending so `firstCategoryName` is the
      // first split (the one the operator sees at the top of the
      // editor), not the row PostgREST returned first.
      rows.sort((a, b) => a.line_number - b.line_number);
      for (const r of rows) {
        const summary =
          map.get(r.transaction_id) ??
          ({ count: 0, firstCategoryName: null, totalCents: 0 } as TransactionSplitSummary);
        summary.count += 1;
        summary.totalCents += r.amount_cents;
        if (summary.firstCategoryName === null) {
          summary.firstCategoryName = r.chart_of_accounts?.name ?? null;
        }
        map.set(r.transaction_id, summary);
      }
      return map;
    },
  });
}

/**
 * One row per bank_accounts row (mapped or not), pairing the live
 * Mercury/Plaid balance with the latest QBO month-end balance for the
 * mapped chart_of_accounts row. Backed by `v_bank_reconciliation`
 * (migration 52). `difference_cents` is null when there is no QBO
 * snapshot to compare against — distinct from "diff is zero".
 *
 * Sign convention on `unposted_signed_cents` is natural-direction
 * relative to the underlying account's balance (positive = pushes the
 * balance up, both for cash accounts and for credit liabilities). The
 * sign-flip between deposit and credit accounts lives in the view's
 * SQL, not the client.
 */
export interface BankReconciliationRow {
  organization_id: string;
  bank_account_id: string;
  bank_account_name: string;
  bank_provider: string;
  bank_institution: string | null;
  bank_account_type: string | null;
  is_credit: boolean;
  currency: string | null;
  current_balance_cents: number;
  available_balance_cents: number | null;
  last_synced_at: string | null;
  qb_account_id: string | null;
  qb_account_code: string | null;
  qb_account_name: string | null;
  qb_account_type: 'asset' | 'liability' | 'equity' | 'revenue' | 'expense' | null;
  qb_account_subtype: string | null;
  qbo_balance_cents: number | null;
  qbo_as_of: string | null;
  difference_cents: number | null;
  unposted_count: number;
  unposted_signed_cents: number;
}

export function useBankReconciliation() {
  const enabled = useFinancialsEnabled();
  return useQuery({
    queryKey: ['bank-reconciliation'],
    enabled,
    queryFn: async () => {
      const { data, error } = await supabase
        .from('v_bank_reconciliation' as never)
        .select('*')
        .order('bank_account_name');
      if (error) throw error;
      return data as unknown as BankReconciliationRow[];
    },
  });
}

/**
 * QBO Chart-of-Accounts rows that LOOK like bank or credit-card
 * registers but have no `bank_accounts.qb_account_id` pointing at
 * them. Surfaced on /financials/reconciliation so the operator
 * notices "Atlas isn't pulling a live feed for this QBO register at
 * all." Backed by `v_qbo_bank_accounts_unmapped` (migration 52).
 */
export interface UnmappedQboBankAccountRow {
  organization_id: string;
  qb_account_id: string;
  qb_account_code: string | null;
  qb_account_name: string;
  qb_account_type: 'asset' | 'liability';
  qb_account_subtype: string | null;
  qbo_balance_cents: number | null;
  qbo_as_of: string | null;
}

export function useUnmappedQboBankAccounts() {
  const enabled = useFinancialsEnabled();
  return useQuery({
    queryKey: ['qbo-bank-accounts-unmapped'],
    enabled,
    queryFn: async () => {
      const { data, error } = await supabase
        .from('v_qbo_bank_accounts_unmapped' as never)
        .select('*')
        .order('qb_account_code', { nullsFirst: false })
        .order('qb_account_name');
      if (error) throw error;
      return data as unknown as UnmappedQboBankAccountRow[];
    },
  });
}
